import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FARM_ANIMALS, soundUrl } from "./gameUtils";

const STORAGE_KEY = "pixley_animal_sound_farm_muted";

// Sound engine for Sound Farm: Barnyard Band. Plays the real animal-sound clips
// (bundled as asf_<id>.mp3) with a speechSynthesis onomatopoeia fallback, and
// runs the band groove: recruited members "sing" in rotation on a bar grid,
// publishing who is singing so the riser can bounce them on their beat. Mystery
// auditions and toybox taps briefly suspend the groove so the child always
// hears one clear voice. Feedback chimes come from Web Audio.
// Ported from AnimalFarmAudio.swift.

const NORMAL_BAR_MS = 2100;
const SHOWTIME_BAR_MS = 1400;
const BIRDS = new Set(["duck", "chicken", "turkey", "rooster"]);

const createAudioContext = () => {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  return Ctx ? new Ctx() : null;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const useFarmSoundManager = () => {
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  // Published so the view can react (speaker pulse / riser bounce).
  const [isPlayingAnimal, setIsPlayingAnimal] = useState(false);
  const [singingID, setSingingID] = useState(null);

  const contextRef = useRef(null);
  const clipsRef = useRef(new Map());
  const currentClipRef = useRef(null);

  const mutedRef = useRef(muted);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  // Band groove internals.
  const bandRef = useRef([]);
  const showtimeRef = useRef(false);
  const bandTokenRef = useRef(null);
  const bandPlayerRef = useRef(null);
  const suspendedUntilRef = useRef(0);

  const getClip = useCallback((animalId) => {
    if (typeof Audio === "undefined") return null;
    let clip = clipsRef.current.get(animalId);
    if (!clip) {
      clip = new Audio(soundUrl(animalId));
      clip.preload = "auto";
      clipsRef.current.set(animalId, clip);
    }
    return clip;
  }, []);

  const ensureAudio = useCallback(async () => {
    if (!contextRef.current) contextRef.current = createAudioContext();
    if (contextRef.current?.state === "suspended") await contextRef.current.resume();
    return contextRef.current;
  }, []);

  const playNotes = useCallback(
    async (frequencies, duration = 0.14, type = "sine") => {
      if (mutedRef.current) return;
      const context = await ensureAudio();
      if (!context) return;
      frequencies.forEach((frequency, index) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const startsAt = context.currentTime + index * 0.075;
        oscillator.type = type;
        oscillator.frequency.value = frequency;
        gain.gain.setValueAtTime(0.055, startsAt);
        gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + duration);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(startsAt);
        oscillator.stop(startsAt + duration);
      });
    },
    [ensureAudio]
  );

  const speakAnimal = useCallback((farmAnimal) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      playNotes([260, 210, 250], 0.2, "triangle");
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(farmAnimal.sound);
    utterance.lang = "en-US";
    utterance.rate = farmAnimal.id === "rooster" ? 1.05 : 0.82;
    utterance.pitch = BIRDS.has(farmAnimal.id) ? 1.45 : 0.82;
    utterance.volume = 1;
    window.speechSynthesis.speak(utterance);
  }, [playNotes]);

  const suspendBand = useCallback((seconds) => {
    suspendedUntilRef.current = Date.now() + seconds * 1000;
    setSingingID(null);
  }, []);

  const stopSolo = useCallback(() => {
    if (currentClipRef.current) {
      currentClipRef.current.pause();
      currentClipRef.current.onended = null;
    }
    currentClipRef.current = null;
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    setIsPlayingAnimal(false);
  }, []);

  // MARK: - Solo voices (auditions and toybox taps)

  const playSolo = useCallback(
    async (farmAnimal, ripple) => {
      if (mutedRef.current || !farmAnimal) return;
      await ensureAudio();
      stopSolo();
      bandPlayerRef.current?.pause();

      const clip = getClip(farmAnimal.id);
      if (!clip) {
        suspendBand(1.6);
        speakAnimal(farmAnimal);
        return;
      }

      const duration = Number.isFinite(clip.duration) && clip.duration > 0 ? clip.duration : 1.4;
      suspendBand(duration + 0.55);
      currentClipRef.current = clip;
      try {
        clip.currentTime = 0;
        clip.volume = 1;
        if (ripple) {
          setIsPlayingAnimal(true);
          clip.onended = () => {
            if (currentClipRef.current === clip) setIsPlayingAnimal(false);
          };
        }
        const played = clip.play();
        if (played && typeof played.catch === "function") {
          await played.catch(() => {
            setIsPlayingAnimal(false);
            speakAnimal(farmAnimal);
          });
        }
      } catch {
        setIsPlayingAnimal(false);
        speakAnimal(farmAnimal);
      }
    },
    [ensureAudio, getClip, speakAnimal, stopSolo, suspendBand]
  );

  const playMystery = useCallback((farmAnimal) => playSolo(farmAnimal, true), [playSolo]);
  const playTapped = useCallback((farmAnimal) => playSolo(farmAnimal, false), [playSolo]);

  // MARK: - The band groove

  const stopBand = useCallback(() => {
    if (bandTokenRef.current) bandTokenRef.current.cancelled = true;
    bandTokenRef.current = null;
    bandPlayerRef.current?.pause();
    bandPlayerRef.current = null;
    setSingingID(null);
  }, []);

  const startBandLoop = useCallback(
    (leadWithLast) => {
      if (bandTokenRef.current) bandTokenRef.current.cancelled = true;
      const members = bandRef.current;
      if (!members.length || mutedRef.current) return;
      const token = { cancelled: false };
      bandTokenRef.current = token;
      const bar = showtimeRef.current ? SHOWTIME_BAR_MS : NORMAL_BAR_MS;
      let index = leadWithLast ? members.length - 1 : 0;

      (async () => {
        while (!token.cancelled) {
          const soloPlaying =
            currentClipRef.current && !currentClipRef.current.paused && !currentClipRef.current.ended;
          if (Date.now() >= suspendedUntilRef.current && !soloPlaying) {
            const farmAnimal = members[index % members.length];
            index += 1;
            const clip = getClip(farmAnimal.id);
            if (clip) {
              try {
                clip.currentTime = 0;
                clip.volume = showtimeRef.current ? 1.0 : 0.72;
                clip.play().catch(() => {});
                bandPlayerRef.current = clip;
              } catch {
                /* keep the groove going even if one clip fails */
              }
            }
            setSingingID(farmAnimal.id);
          }
          await sleep(bar);
          if (token.cancelled) return;
          setSingingID(null);
          await sleep(250);
        }
      })();
    },
    [getClip]
  );

  const setBand = useCallback(
    (animals, showtime = false) => {
      bandRef.current = animals || [];
      showtimeRef.current = showtime;
      if (!bandRef.current.length) {
        stopBand();
      } else if (!mutedRef.current) {
        startBandLoop(true);
      }
    },
    [startBandLoop, stopBand]
  );

  // MARK: - Chimes

  const playCorrect = useCallback(() => playNotes([590, 790, 1040], 0.18, "triangle"), [playNotes]);
  const playWrong = useCallback(() => playNotes([260, 210], 0.16, "sine"), [playNotes]);
  const playCelebration = useCallback(
    () => playNotes([523, 659, 784, 1047], 0.24, "triangle"),
    [playNotes]
  );

  // MARK: - Control

  const stop = useCallback(() => {
    stopSolo();
    stopBand();
  }, [stopSolo, stopBand]);

  const toggleMute = useCallback(() => {
    setMuted((current) => {
      const next = !current;
      mutedRef.current = next;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* the game still works when storage is unavailable */
      }
      if (next) {
        stopSolo();
        stopBand();
      } else if (bandRef.current.length) {
        startBandLoop(false);
      }
      return next;
    });
  }, [startBandLoop, stopBand, stopSolo]);

  const prewarm = useCallback(async () => {
    const context = await ensureAudio();
    FARM_ANIMALS.forEach((farmAnimal) => {
      const clip = getClip(farmAnimal.id);
      if (clip) clip.load();
    });
    return context;
  }, [ensureAudio, getClip]);

  useEffect(() => () => {
    if (bandTokenRef.current) bandTokenRef.current.cancelled = true;
    if (currentClipRef.current) currentClipRef.current.pause();
    if (bandPlayerRef.current) bandPlayerRef.current.pause();
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  }, []);

  return useMemo(
    () => ({
      muted,
      isPlayingAnimal,
      singingID,
      toggleMute,
      playMystery,
      playTapped,
      playCorrect,
      playWrong,
      playCelebration,
      setBand,
      stopBand,
      stop,
      prewarm,
    }),
    [
      muted, isPlayingAnimal, singingID, toggleMute, playMystery, playTapped,
      playCorrect, playWrong, playCelebration, setBand, stopBand, stop, prewarm,
    ]
  );
};
